/* eslint-disable no-nested-ternary */
import { useEffect, useState, useRef, useCallback } from 'react';
import cx from 'clsx';
import { Box, Collapse } from '@mui/material';
import { useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { Badge, badgeStyle, badgeType, Text } from '../../../../../packages';
import { reportItemProgress } from '../AddNewReport.constants';
import MedicationForm from '../medication-form';
// styles
import { useStyles } from './ReportFormCollapse.styles';
import { getAuth } from '../../../../../redux/modules/auth/selectors';
import { getProfile } from '../../../../../redux/modules/profile/selectors';
import { MedicationEditSchema } from '../index.schema';
import debounce from 'debounce';

const defaultValues = {
  status: 'TO_BE_PRESCRIBED',
  quantity: '',
  dose: '',
  frequency: '',
  allowSubstitutions: true,
  pharmacyNotes: '',
  duration: '',
  sig: null,
  symptoms: [],
  dispenseAmount: '',
  provider: 'Confidant Provider',
  associatedDiagnoses: [],
  confidantProvider: '',
  externalMedicationId: '',
  externalMedicationName: '',
  providerName: '',
  additionalNotes: '',
  refills: 0,
  isExternalProvider: false,
};

const ReportFormCollapseMedications = ({
  open = false,
  medication,
  onChange,
  lookupData,
  onOpenCollapse,
  patientDiagnoses,
  options,
}) => {
  const { providers } = useSelector(getProfile);
  const {
    meta: { authority, userId },
  } = useSelector(getAuth);

  const [collapsed, setCollapsed] = useState(open || false);
  const [state, setState] = useState(defaultValues);
  const classes = useStyles();
  const prevStateRef = useRef();

  useEffect(() => {
    if (providers.length > 0 && authority === 'PRACTITIONER') {
      const currentProvider = providers.find(provider => provider.providerId === userId);
      if (currentProvider) {
        setState(prevState => ({
          ...prevState,
          confidantProvider: currentProvider.providerId,
        }));
      }
    }
  }, [authority, providers, userId]);

  useEffect(() => {
    // For admin users, always check the isExternalProvider checkbox
    if (authority !== 'PRACTITIONER') {
      setState(prevState => ({
        ...prevState,
        isExternalProvider: true,
      }));
    }
  }, [authority]);

  useEffect(() => {
    setCollapsed(open || false);
  }, [open]);

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
    onOpenCollapse && onOpenCollapse(!collapsed);
  };

  const form = useFormik({
    initialValues: { ...defaultValues },
    validationSchema: MedicationEditSchema,
    onSubmit: payload => {
      console.log({ payload });
    },
  });

  useEffect(() => {
    if (medication) {
      const { ...remaining } = medication;
      const newObj = {
        ...remaining,
      };

      // Populate both formik form and component state
      Object.keys(newObj).forEach(async key => {
        await form.setFieldValue(key, newObj[key]);
      });

      // Populate the component state that the form actually uses
      setState(prevState => ({
        ...prevState,
        ...newObj,
      }));

      if (medication.progress) {
        setCollapsed(medication.progress === reportItemProgress.InProgress);
      }
    }
  }, [medication]);

  const onChangeHandler = useCallback(
    (key, value) => {
      setState(prevState => ({
        ...prevState,
        [key]: value,
      }));

      // Also update formik form to keep it in sync
      form.setFieldValue(key, value);
    },
    [form],
  );

  // Debounced function to update parent with changes
  const debouncedUpdateParent = useCallback(
    debounce(updatedMedication => {
      onChange(updatedMedication);
    }, 300),
    [onChange],
  );

  // Save form data to parent only when there are meaningful changes
  useEffect(() => {
    // Skip initial render
    if (!prevStateRef.current) {
      prevStateRef.current = state;
      return;
    }

    // Check if there are actual changes in important fields
    const importantFields = [
      'externalRx',
      'quantity',
      'dose',
      'frequency',
      'duration',
      'sig',
      'dispenseAmount',
      'refills',
      'allowSubstitutions',
      'pharmacyNotes',
      'additionalNotes',
      'provider',
      'confidantProvider',
      'providerName',
      'associatedDiagnoses',
      'symptoms',
      'isExternalProvider',
    ];

    const hasChanges = importantFields.some(field => {
      const prevValue = prevStateRef.current[field];
      const currentValue = state[field];

      if (field === 'externalRx') {
        return prevValue?.value !== currentValue?.value;
      }
      if (field === 'associatedDiagnoses' || field === 'symptoms') {
        // Deep compare arrays
        return JSON.stringify(prevValue) !== JSON.stringify(currentValue);
      }
      return prevValue !== currentValue;
    });

    if (hasChanges) {
      const updatedMedication = {
        ...medication,
        ...state,
        // Map additionalNotes to notes for backward compatibility
        notes: state.additionalNotes || '',
        externalMedicationId: state.externalRx?.value,
        externalMedicationName: state.externalRx?.label,
      };
      debouncedUpdateParent(updatedMedication);
    }

    // Update prevStateRef after processing
    prevStateRef.current = state;
  }, [state, medication, debouncedUpdateParent]);

  // Cleanup debounced function on unmount
  useEffect(() => {
    return () => {
      debouncedUpdateParent.clear();
    };
  }, [debouncedUpdateParent]);

  return (
    <Box className={classes.root}>
      <Box className={cx(classes.header, { [classes.headerBorder]: collapsed })} onClick={toggleCollapsed}>
        <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
          <Text className={classes.headTitle}>{medication.item.label}</Text>
          <Badge
            variant={badgeType.OUTLINED}
            className={classes.badge}
            style={
              medication.progress === reportItemProgress.Completed
                ? badgeStyle.SUCCESS
                : medication.progress === reportItemProgress.Incomplete
                ? badgeStyle.MISREPORTED
                : badgeStyle.CRITICAL
            }
          >
            {medication.progress}
          </Badge>
        </Box>
      </Box>
      <Collapse in={collapsed}>
        <form className={classes.form}>
          <MedicationForm
            form={form}
            medication={medication}
            lookupData={lookupData}
            options={options}
            patientDiagnoses={patientDiagnoses}
            onChangeHandler={onChangeHandler}
            state={state}
          />
        </form>
      </Collapse>
    </Box>
  );
};

export default ReportFormCollapseMedications;
